import { PaintCollection } from "@omni/types/api/paintColors/bff/paintCollection.model";
import { RequireChildrenFor } from "@omni/types/components/utils/children.types";
import { Mapped } from "@omni/utils/context/mapped.context";
import { isDefined } from "@omni/utils/helper.utils";
import { createContext, use, useMemo } from "react";

import {
    blendColorsLinear,
    getColorRange,
    sortColorsByLuminance
} from "@/components/PaintConfigurator/helpers/color.helper";

type FamiliesContextValue = {
    families: Array<{ name: string; hex: string; topColors: Array<string> }>;
};

type FamiliesContextProviderProps = RequireChildrenFor<{
    selectedCollection?: PaintCollection;
}>;

export const FamiliesContext = createContext<FamiliesContextValue | undefined>(
    undefined
);
FamiliesContext.displayName = "FamiliesContext";

export const FamiliesContextProvider = ({
    selectedCollection,
    children
}: FamiliesContextProviderProps) => {
    const families = useMemo(() => {
        if (!isDefined(selectedCollection)) {
            return [];
        }

        const mappedFamilies = selectedCollection.families
            .map((family) => {
                const colors = family.colors.map((color) => color.hexValue);
                const hex = blendColorsLinear(colors);
                const topColors = getColorRange(colors, 4);
                if (!hex) return undefined;
                return {
                    name: family.name,
                    hex,
                    topColors
                };
            })
            .filter(isDefined);

        return sortColorsByLuminance(mappedFamilies, (f) => f.hex);
    }, [selectedCollection]);

    return (
        <FamiliesContext value={{ families }}>
            <Mapped array={families}>{children}</Mapped>
        </FamiliesContext>
    );
};

export const useFamiliesContext = () => {
    const context = use<FamiliesContextValue | undefined>(FamiliesContext);

    if (!isDefined(context)) {
        throw new Error(`${FamiliesContext.displayName} is undefined.`);
    }

    return context;
};
